/*
 * File: GetSectionEnrollments.ts
 * Author: Austin Robinson
 * Date Created: 8/3/2023
 * Copyright (c) DrivingDoc 2023
 */
import { createQuery } from 'react-query-kit';

import { PostGreSQLInterval, privateApi } from 'api';

export type GetSectionEnrollmentsParams = {
  sectionId: string;
  page: number;
  pageSize: number;
  sortModel?: {
    columnName: string;
    sortDir: 'asc' | 'desc';
  }[];
};

export type SectionEnrollment = {
  id: string;
  course_section_id: string;
  student_charge_id: string | null;
  package_course_package_id: string | null;
  package_course_course_id: string | null;
  secure_seat_mode: 'at_time_of_enrollment' | 'when_deposit_paid' | 'when_full_cost_paid';
  seat_secured_at: boolean;
  enrolled_at: string;
  created_by: string | null;
  completed_at: string | null;
  must_complete_by: string | null;
  user: {
    id: string;
    title: string | null;
    first_name: string | null;
    middle_name: string | null;
    last_name: string | null;
    nickname: string | null;
    email: string | null;
    date_of_birth: string | null;
    sex: string | null;
    active: boolean;
  };
  course_policy: {
    tenant_id: string;
    course_id: string;
    updated_at: string;
    min_time_to_complete: PostGreSQLInterval | null;
    max_time_to_complete: PostGreSQLInterval | null;
    allow_self_service_extensions: boolean | null;
    standard_extension_time: PostGreSQLInterval | null;
    standard_extension_fee: number | null;
    max_extensions: number | null;
    max_time_to_request_extension: PostGreSQLInterval | null;
    absences_allowed_before_fee: number | null;
    absence_fee: number | null;
    absence_fee_due: PostGreSQLInterval | null;
    absence_fee_due_lock: PostGreSQLInterval | null;
    no_permits_allowed_before_fee: number | null;
    no_permit_fee: number | null;
    no_permit_fee_due: PostGreSQLInterval | null;
    no_permit_fee_due_lock: PostGreSQLInterval | null;
    allow_cancellations: boolean | null;
    cancellations_allowed_before_fee: number | null;
    cancellation_fee: number | null;
    cancellation_fee_due: PostGreSQLInterval | null;
    cancellation_fee_due_lock: PostGreSQLInterval | null;
    late_cancellations_allowed_before_fee: number | null;
    late_cancellation_fee: number | null;
    late_cancellation_fee_due: PostGreSQLInterval | null;
    late_cancellation_fee_due_lock: PostGreSQLInterval | null;
    late_cancellation_deadline: PostGreSQLInterval | null;
  };
  course_completion_criteria: {
    course_id: string | null;
    updated_at: string | null;
    performance_time_required: PostGreSQLInterval;
    observation_time_required: PostGreSQLInterval;
  };
  performance_time_completed: PostGreSQLInterval;
  performance_time_scheduled: PostGreSQLInterval;
  observation_time_completed: PostGreSQLInterval;
  observation_time_scheduled: PostGreSQLInterval;
};

export type GetSectionEnrollmentsResult = {
  rows: SectionEnrollment[];
  count: number;
};

async function getSectionEnrollments(params: GetSectionEnrollmentsParams): Promise<GetSectionEnrollmentsResult> {
  // make api call
  const result = await privateApi.post(
    `${import.meta.env.VITE_API_BASE_URL}/courses/sections/${params.sectionId}/enrollments`,
    {
      sortModel: params?.sortModel ?? [],
    },
    {
      params: {
        page: params.page,
        pageSize: params.pageSize,
      },
    },
  );

  // return the data object
  return result.data as GetSectionEnrollmentsResult;
}

const useGetSectionEnrollments = createQuery<GetSectionEnrollmentsResult, GetSectionEnrollmentsParams, Error>({
  queryKey: ['section_enrollments'],
  fetcher: (variables) => getSectionEnrollments(variables),
});

export { getSectionEnrollments, useGetSectionEnrollments };
